import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './App.css';
import TreasuryABI from './contracts/Treasury.json';
import localhostDeployment from './contracts/localhost.json';
import sepoliaDeployment from './contracts/sepolia.json';

const DEPLOYMENTS = {
  '0xaa36a7': sepoliaDeployment,
  '0x7a69':   localhostDeployment,
};

const TREASURY_ABI = TreasuryABI.abi;

// ─── Color tokens ─────────────────────────────────────────────────────────────
const BLUE      = '#38bdf8';   // electric baby blue — primary action
const BLUE_LT   = '#7dd3fc';   // soft steel blue — accents and borders
const WHITE     = '#ffffff';   // primary text
const MUTED     = '#94a3b8';   // muted steel — subtext
const GREEN     = '#22c55e';   // success green
const CARD_BG   = 'rgba(56, 189, 248, 0.06)';
const CARD_BDR  = 'rgba(125, 211, 252, 0.2)';

const STATUS_COLORS = {
  pending: { backgroundColor: 'rgba(56, 189, 248, 0.2)', color: WHITE },
  success: { backgroundColor: 'rgba(34, 197, 94, 0.25)', color: GREEN },
  error:   { backgroundColor: 'rgba(127, 29, 29, 0.4)', color: '#fca5a5' },
  default: { backgroundColor: 'rgba(125, 211, 252, 0.1)', color: BLUE_LT },
};

const parseError = (err) => {
  if (err.message.includes('user rejected'))            return 'Transaction rejected in MetaMask.';
  if (err.message.includes('insufficient funds'))       return 'Insufficient funds for this transaction.';
  if (err.message.includes('Not the treasury owner'))   return 'Only the treasury owner can withdraw funds.';
  if (err.message.includes('Insufficient ETH balance')) return 'Insufficient ETH balance in treasury.';
  if (err.message.includes('Insufficient token balance')) return 'Insufficient token balance in treasury.';
  if (err.message.includes('Amount must be greater'))   return 'Amount must be greater than zero.';
  if (err.message.includes('Invalid address'))          return 'Invalid address.';
  if (err.message.includes('Invalid token address'))    return 'Invalid token address.';
  return 'Transaction failed. Please try again.';
};

function Spinner() {
  return (
    <span style={{
      display: 'inline-block',
      width: '16px', height: '16px',
      border: '2px solid rgba(125, 211, 252, 0.3)',
      borderTop: `2px solid ${BLUE_LT}`,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      marginRight: '10px',
      verticalAlign: 'middle',
    }} />
  );
}

function App() {
  const [treasuryContract,  setTreasuryContract]  = useState(null);
  const [readTreasury,      setReadTreasury]      = useState(null);
  const [account,           setAccount]           = useState(null);
  const [chainId,           setChainId]           = useState(null);
  const [treasuryAddress,   setTreasuryAddress]   = useState('');

  // contract info
  const [treasuryName,      setTreasuryName]      = useState('');
  const [owner,             setOwner]             = useState('');
  const [ethBalance,        setEthBalance]        = useState('0');
  const [isOwner,           setIsOwner]           = useState(false);

  // token balances
  const [tokenAddress,      setTokenAddress]      = useState('');
  const [tokenBalance,      setTokenBalance]      = useState(null);
  const [tokenSymbol,       setTokenSymbol]       = useState('');
  const [tokenName,         setTokenName]         = useState('');

  // deposit token form
  const [depositTokenAddress, setDepositTokenAddress] = useState('');
  const [depositAmount,       setDepositAmount]       = useState('');
  const [depositSymbol,       setDepositSymbol]       = useState('');
  const [depositName,         setDepositName]         = useState('');

  // withdraw forms
  const [withdrawETHTo,     setWithdrawETHTo]     = useState('');
  const [withdrawETHAmount, setWithdrawETHAmount] = useState('');
  const [withdrawTokenAddr, setWithdrawTokenAddr] = useState('');
  const [withdrawTokenTo,   setWithdrawTokenTo]   = useState('');
  const [withdrawTokenAmt,  setWithdrawTokenAmt]  = useState('');

  // status
  const [status,            setStatus]            = useState('');
  const [statusStyle,       setStatusStyle]       = useState(STATUS_COLORS.default);
  const [isLoading,         setIsLoading]         = useState(false);
  const [txHash,            setTxHash]            = useState('');

  // ── load data ───────────────────────────────────────────────────────────────

  const loadDashboardData = useCallback(async (_readTreasury, _account) => {
    try {
      const _name     = await _readTreasury.name();
      const _owner    = await _readTreasury.owner();
      const _ethBal   = await _readTreasury.getETHBalance();
      const _isOwner  = _account.toLowerCase() === _owner.toLowerCase();

      setTreasuryName(_name);
      setOwner(_owner);
      setEthBalance(ethers.utils.formatEther(_ethBal));
      setIsOwner(_isOwner);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, []);

  // ── connect wallet ──────────────────────────────────────────────────────────

  const connectWallet = useCallback(async () => {
    try {
      if (!window.ethereum) {
        setStatus('MetaMask not found. Please install it.');
        setStatusStyle(STATUS_COLORS.error);
        return;
      }

      const _chainId = await window.ethereum.request({ method: 'eth_chainId' });

      if (_chainId !== '0xaa36a7' && _chainId !== '0x7a69') {
        setStatus('Please switch MetaMask to Sepolia or Localhost 8545.');
        setStatusStyle(STATUS_COLORS.error);
        return;
      }

      const deployment        = DEPLOYMENTS[_chainId];
      const _treasuryAddress  = deployment.Treasury.address;

      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer   = provider.getSigner();
      const _account = await signer.getAddress();

      const isLocalhost = _chainId === '0x7a69';
      const rpc = isLocalhost
        ? new ethers.providers.JsonRpcProvider('http://127.0.0.1:8545')
        : new ethers.providers.JsonRpcProvider(
            process.env.REACT_APP_ALCHEMY_URL,
            { name: 'sepolia', chainId: 11155111 }
          );

      const _treasuryContract = new ethers.Contract(_treasuryAddress, TREASURY_ABI, signer);
      const _readTreasury     = new ethers.Contract(_treasuryAddress, TREASURY_ABI, rpc);

      setTreasuryContract(_treasuryContract);
      setReadTreasury(_readTreasury);
      setAccount(_account);
      setChainId(_chainId);
      setTreasuryAddress(_treasuryAddress);

      await loadDashboardData(_readTreasury, _account);
    } catch (err) {
      setStatus('Error connecting wallet: ' + err.message);
      setStatusStyle(STATUS_COLORS.error);
    }
  }, [loadDashboardData]);

  // ── account change listener ─────────────────────────────────────────────────

  useEffect(() => {
    if (!window.ethereum) return;
    const handle = async (accounts) => {
      setStatus(''); setTxHash('');
      if (accounts.length === 0) {
        setAccount(null); setTreasuryContract(null); setReadTreasury(null);
        setTreasuryName(''); setOwner(''); setEthBalance('0');
      } else {
        await connectWallet();
      }
    };
    window.ethereum.on('accountsChanged', handle);
    return () => window.ethereum.removeListener('accountsChanged', handle);
  }, [connectWallet]);

  // ── token preview when address typed ───────────────────────────────────────

  useEffect(() => {
    if (!ethers.utils.isAddress(depositTokenAddress) || !readTreasury) {
      setDepositSymbol(''); setDepositName('');
      return;
    }
    (async () => {
      try {
        const t = new ethers.Contract(depositTokenAddress, [
          'function symbol() view returns (string)',
          'function name() view returns (string)',
        ], readTreasury.provider);
        setDepositSymbol(await t.symbol());
        setDepositName(await t.name());
      } catch (_) {
        setDepositSymbol('?'); setDepositName('Unknown Token');
      }
    })();
  }, [depositTokenAddress, readTreasury]);

  // ── check token balance ─────────────────────────────────────────────────────

  const handleCheckTokenBalance = async () => {
    if (!ethers.utils.isAddress(tokenAddress)) {
      setStatus('Please enter a valid token address.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    try {
      const bal = await readTreasury.getTokenBalance(tokenAddress);
      const t = new ethers.Contract(tokenAddress, [
        'function symbol() view returns (string)',
        'function name() view returns (string)',
      ], readTreasury.provider);
      const sym  = await t.symbol();
      const name = await t.name();
      setTokenBalance(ethers.utils.formatUnits(bal, 18));
      setTokenSymbol(sym);
      setTokenName(name);
    } catch (err) {
      setStatus('Could not fetch token balance.'); setStatusStyle(STATUS_COLORS.error);
    }
  };

  // ── deposit token ───────────────────────────────────────────────────────────

  const handleDepositToken = async () => {
    if (!ethers.utils.isAddress(depositTokenAddress)) {
      setStatus('Please enter a valid token address.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    if (!depositAmount || Number(depositAmount) <= 0) {
      setStatus('Please enter an amount greater than zero.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    try {
      setStatus('Approving token transfer...'); setStatusStyle(STATUS_COLORS.pending); setIsLoading(true);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer   = provider.getSigner();
      const tokenContract = new ethers.Contract(depositTokenAddress, [
        'function approve(address spender, uint256 amount) returns (bool)',
      ], signer);

      const amount = ethers.utils.parseUnits(depositAmount, 18);
      const approveTx = await tokenContract.approve(treasuryAddress, amount);
      await approveTx.wait();

      setStatus('Depositing tokens...');
      const depositTx = await treasuryContract.depositToken(depositTokenAddress, amount);
      await depositTx.wait();
      await new Promise(r => setTimeout(r, 2000));

      setIsLoading(false);
      setTxHash(depositTx.hash);
      setStatus('Tokens deposited successfully!');
      setStatusStyle(STATUS_COLORS.success);
      setDepositTokenAddress(''); setDepositAmount('');
      setDepositSymbol(''); setDepositName('');
      await loadDashboardData(readTreasury, account);
    } catch (err) {
      setIsLoading(false); setTxHash('');
      setStatus(parseError(err)); setStatusStyle(STATUS_COLORS.error);
    }
  };

  // ── withdraw ETH ────────────────────────────────────────────────────────────

  const handleWithdrawETH = async () => {
    if (!ethers.utils.isAddress(withdrawETHTo)) {
      setStatus('Please enter a valid destination address.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    if (!withdrawETHAmount || Number(withdrawETHAmount) <= 0) {
      setStatus('Please enter an amount greater than zero.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    try {
      setStatus('Withdrawing ETH...'); setStatusStyle(STATUS_COLORS.pending); setIsLoading(true);
      const amount = ethers.utils.parseEther(withdrawETHAmount);
      const tx = await treasuryContract.withdrawETH(withdrawETHTo, amount);
      await tx.wait();
      await new Promise(r => setTimeout(r, 2000));
      setIsLoading(false);
      setTxHash(tx.hash);
      setStatus('ETH withdrawn successfully!');
      setStatusStyle(STATUS_COLORS.success);
      setWithdrawETHTo(''); setWithdrawETHAmount('');
      await loadDashboardData(readTreasury, account);
    } catch (err) {
      setIsLoading(false); setTxHash('');
      setStatus(parseError(err)); setStatusStyle(STATUS_COLORS.error);
    }
  };

  // ── withdraw token ──────────────────────────────────────────────────────────

  const handleWithdrawToken = async () => {
    if (!ethers.utils.isAddress(withdrawTokenAddr)) {
      setStatus('Please enter a valid token address.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    if (!ethers.utils.isAddress(withdrawTokenTo)) {
      setStatus('Please enter a valid destination address.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    if (!withdrawTokenAmt || Number(withdrawTokenAmt) <= 0) {
      setStatus('Please enter an amount greater than zero.'); setStatusStyle(STATUS_COLORS.error); return;
    }
    try {
      setStatus('Withdrawing tokens...'); setStatusStyle(STATUS_COLORS.pending); setIsLoading(true);
      const amount = ethers.utils.parseUnits(withdrawTokenAmt, 18);
      const tx = await treasuryContract.withdrawToken(withdrawTokenAddr, withdrawTokenTo, amount);
      await tx.wait();
      await new Promise(r => setTimeout(r, 2000));
      setIsLoading(false);
      setTxHash(tx.hash);
      setStatus('Tokens withdrawn successfully!');
      setStatusStyle(STATUS_COLORS.success);
      setWithdrawTokenAddr(''); setWithdrawTokenTo(''); setWithdrawTokenAmt('');
      await loadDashboardData(readTreasury, account);
    } catch (err) {
      setIsLoading(false); setTxHash('');
      setStatus(parseError(err)); setStatusStyle(STATUS_COLORS.error);
    }
  };

  // ── refresh ─────────────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    if (!readTreasury || !account) return;
    setStatus('Refreshing...'); setStatusStyle(STATUS_COLORS.default);
    await loadDashboardData(readTreasury, account);
    setStatus('');
  };

  const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="shimmer-bg"></div>
      <div className="content min-h-screen p-8">
        <div className="max-w-4xl mx-auto" style={{ position: 'relative' }}>

          {/* TD LOGO */}
          <img src="/td-logo-justtd.png" alt="Tredway Development"
            style={{ position: 'absolute', top: '0', left: '-110px', height: '35px' }} />

          {/* HEADER */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-5xl font-bold tracking-tight" style={{ color: WHITE }}>
                Token <span style={{ color: BLUE }}>Treasury</span> Dashboard
              </h1>
              <p className="text-sm mt-2 uppercase tracking-widest font-medium" style={{ color: MUTED }}>
                {treasuryName ? treasuryName : 'Secure Fund Management — Multisig Controlled'}
              </p>
            </div>
            {account && (
              <div className="text-right">
                <button onClick={handleRefresh} disabled={isLoading}
                  className="text-xs font-mono px-3 py-1 rounded-lg mb-2 transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'rgba(125, 211, 252, 0.1)',
                    border: `1px solid ${CARD_BDR}`,
                    color: BLUE_LT,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'block',
                    marginLeft: 'auto',
                  }}>
                  ↻ Refresh
                </button>
                <p className="text-xs font-mono" style={{ color: MUTED }}>Connected</p>
                <p className="text-sm font-mono font-semibold" style={{ color: BLUE_LT }}>
                  {account.slice(0, 6)}...{account.slice(-4)}
                </p>
                {isOwner && (
                  <p className="text-xs mt-1 font-semibold" style={{ color: GREEN }}>✓ Owner</p>
                )}
              </div>
            )}
          </div>
          <hr style={{ borderColor: 'rgba(125, 211, 252, 0.15)', marginBottom: '2rem' }} />

          {/* STATUS BAR */}
          {status && (
            <div className="mb-6 p-4 rounded-xl text-sm font-medium flex items-center gap-2 transition-all"
              style={statusStyle}>
              {isLoading && <Spinner />}
              <span>{status}</span>
              {txHash && !isLoading && chainId === '0xaa36a7' && (
                <a href={`https://sepolia.etherscan.io/tx/${txHash}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: BLUE_LT, textDecoration: 'underline', marginLeft: '8px', fontWeight: 'bold' }}>
                  View on Etherscan ↗
                </a>
              )}
            </div>
          )}

          {/* NOT CONNECTED */}
          {!account ? (
            <div className="text-center py-32">
              <div className="mb-6 text-6xl">🏦</div>
              <button onClick={connectWallet}
                className="px-8 py-4 rounded-xl font-semibold text-white text-lg transition-all hover:opacity-90 mb-6 btn-hover"
                style={{ backgroundColor: BLUE }}>
                Connect Wallet
              </button>
              <p className="text-3xl font-bold mb-3 tracking-tight" style={{ color: WHITE }}>
                Connect your wallet to view the treasury
              </p>
              <p className="text-sm uppercase tracking-widest" style={{ color: MUTED }}>
                Make sure you're on the Sepolia test network or Localhost 8545
              </p>
            </div>
          ) : (
            <>
              {/* STAT CARDS */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { label: 'ETH Balance',  value: fmt(ethBalance) + ' ETH' },
                  { label: 'Owner',        value: owner ? owner.slice(0, 6) + '...' + owner.slice(-4) : '—' },
                  { label: 'Network',      value: chainId === '0xaa36a7' ? 'Sepolia' : 'Localhost' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-2xl p-4 shadow-sm card-hover"
                    style={{
                      backgroundColor: CARD_BG,
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      border: `1px solid ${CARD_BDR}`,
                      borderLeft: `4px solid ${BLUE}`,
                    }}>
                    <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>{stat.label}</p>
                    <p className="text-lg font-bold" style={{ color: WHITE }}>{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* CHECK TOKEN BALANCE */}
              <div className="rounded-2xl p-6 mb-8 shadow-sm card-hover"
                style={{
                  backgroundColor: CARD_BG,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: `1px solid ${CARD_BDR}`,
                  borderLeft: `4px solid ${BLUE}`,
                }}>
                <h2 className="text-lg font-bold mb-4" style={{ color: WHITE }}>Check Token Balance</h2>

                <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Token Address</p>
                <div className="flex gap-3 mb-3">
                  <input type="text" placeholder="0x... ERC-20 token address"
                    value={tokenAddress} onChange={(e) => setTokenAddress(e.target.value)}
                    className="flex-1 border rounded-xl px-4 py-3 text-sm outline-none"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />
                  <button onClick={handleCheckTokenBalance} disabled={isLoading}
                    className="px-5 py-3 rounded-xl font-semibold text-white transition-all btn-hover"
                    style={{ backgroundColor: BLUE, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                    Check
                  </button>
                </div>

                {tokenBalance !== null && (
                  <div className="rounded-xl p-4"
                    style={{ backgroundColor: 'rgba(56, 189, 248, 0.08)', border: `1px solid ${CARD_BDR}` }}>
                    <p className="text-sm font-bold" style={{ color: WHITE }}>
                      {tokenName}
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-mono"
                        style={{ backgroundColor: `${BLUE}20`, color: BLUE_LT }}>
                        {tokenSymbol}
                      </span>
                    </p>
                    <p className="text-2xl font-bold mt-1" style={{ color: BLUE }}>
                      {fmt(tokenBalance)} {tokenSymbol}
                    </p>
                  </div>
                )}
              </div>

              {/* DEPOSIT TOKEN */}
              <div className="rounded-2xl p-6 mb-8 shadow-sm card-hover"
                style={{
                  backgroundColor: CARD_BG,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: `1px solid ${CARD_BDR}`,
                  borderLeft: `4px solid ${BLUE}`,
                }}>
                <h2 className="text-lg font-bold mb-4" style={{ color: WHITE }}>Deposit Tokens</h2>

                <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Token Address</p>
                <input type="text" placeholder="0x... ERC-20 token address"
                  value={depositTokenAddress} onChange={(e) => setDepositTokenAddress(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-2"
                  style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                {depositName && (
                  <p className="text-xs mb-3" style={{ color: GREEN, fontWeight: 600 }}>
                    ✓ {depositName} ({depositSymbol})
                  </p>
                )}

                <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Amount</p>
                <input type="number" placeholder="e.g. 1000"
                  value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-6"
                  style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                <button onClick={handleDepositToken} disabled={isLoading}
                  className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 btn-hover"
                  style={{
                    backgroundColor: BLUE,
                    opacity: isLoading ? 0.6 : 1,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}>
                  ↓ Deposit Tokens
                </button>

                <p className="text-xs mt-3" style={{ color: MUTED }}>
                  Anyone can deposit tokens into the treasury. Only the owner can withdraw.
                </p>
              </div>

              {/* WITHDRAW ETH — owner only */}
              {isOwner && (
                <div className="rounded-2xl p-6 mb-8 shadow-sm card-hover"
                  style={{
                    backgroundColor: CARD_BG,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: `1px solid ${CARD_BDR}`,
                    borderLeft: `4px solid ${GREEN}`,
                  }}>
                  <h2 className="text-lg font-bold mb-4" style={{ color: WHITE }}>Withdraw ETH</h2>

                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Destination Address</p>
                  <input type="text" placeholder="0x... destination address"
                    value={withdrawETHTo} onChange={(e) => setWithdrawETHTo(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-4"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Amount (ETH)</p>
                  <input type="number" placeholder="e.g. 0.5"
                    value={withdrawETHAmount} onChange={(e) => setWithdrawETHAmount(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-6"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                  <button onClick={handleWithdrawETH} disabled={isLoading}
                    className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 btn-hover"
                    style={{
                      backgroundColor: GREEN,
                      opacity: isLoading ? 0.6 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}>
                    ↑ Withdraw ETH
                  </button>
                </div>
              )}

              {/* WITHDRAW TOKEN — owner only */}
              {isOwner && (
                <div className="rounded-2xl p-6 mb-8 shadow-sm card-hover"
                  style={{
                    backgroundColor: CARD_BG,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: `1px solid ${CARD_BDR}`,
                    borderLeft: `4px solid ${GREEN}`,
                  }}>
                  <h2 className="text-lg font-bold mb-4" style={{ color: WHITE }}>Withdraw Tokens</h2>

                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Token Address</p>
                  <input type="text" placeholder="0x... ERC-20 token address"
                    value={withdrawTokenAddr} onChange={(e) => setWithdrawTokenAddr(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-4"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Destination Address</p>
                  <input type="text" placeholder="0x... destination address"
                    value={withdrawTokenTo} onChange={(e) => setWithdrawTokenTo(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-4"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: MUTED }}>Amount</p>
                  <input type="number" placeholder="e.g. 1000"
                    value={withdrawTokenAmt} onChange={(e) => setWithdrawTokenAmt(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none mb-6"
                    style={{ borderColor: CARD_BDR, color: WHITE, backgroundColor: 'rgba(56, 189, 248, 0.08)' }} />

                  <button onClick={handleWithdrawToken} disabled={isLoading}
                    className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 btn-hover"
                    style={{
                      backgroundColor: GREEN,
                      opacity: isLoading ? 0.6 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}>
                    ↑ Withdraw Tokens
                  </button>
                </div>
              )}

              {/* NON OWNER MESSAGE */}
              {!isOwner && (
                <div className="rounded-2xl p-6 mb-8"
                  style={{
                    backgroundColor: 'rgba(56, 189, 248, 0.05)',
                    border: `1px solid ${CARD_BDR}`,
                  }}>
                  <p className="text-sm" style={{ color: MUTED }}>
                    Withdrawals are controlled by the treasury owner at{' '}
                    <span className="font-mono" style={{ color: BLUE_LT }}>
                      {owner.slice(0, 6)}...{owner.slice(-4)}
                    </span>
                    . Connect with the owner wallet to withdraw funds.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;